/* eslint-disable no-console */
import './agenda-appointment-modal.scss';

import { useMutation } from '@apollo/client';
import { usePrevious } from '@uidotdev/usehooks';
import { first, isEmpty, kebabCase, last } from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormProvider } from 'react-hook-form';
import { useIntl } from 'react-intl';

import { useAppContext } from '../../../../data/context/app-context';
import {
  useAgendaAppointmentTypeValues,
  useAgendaTemplateFrequencyValues,
} from '../../../../data/data/agenda/select-values';
import { useAgendaAppointmentNotes } from '../../../../data/data/agenda/use-agenda-appointment-notes';
import { useAgendaAvailableItems } from '../../../../data/data/agenda/use-agenda-available-items';
import { useAgendaFirstAvailableItem } from '../../../../data/data/agenda/use-agenda-first-available-item';
import { useAvailableBulkMessageItems } from '../../../../data/data/bulk-messages/use-available-bulk-message-items';
import { ConversationSelectType } from '../../../../data/data/conversations/enums';
import { useConversationSelectTypeValues } from '../../../../data/data/conversations/select-values';
import { AgendaAppointmentModalAction } from '../../../../data/data/enums';
import { useHealthProfessionalItem } from '../../../../data/data/health-professionals/use-health-professional-item';
import { useHealthProfessionalItems } from '../../../../data/data/health-professionals/use-health-professional-items';
import { useAvailableAppointmentHonorificBlockMinuteValues } from '../../../../data/data/honorific-block-times/use-available-appointment-honorific-block-minute-values';
import { usePatientConversationItems } from '../../../../data/data/patients/use-patient-conversation-items';
import { usePatientItem } from '../../../../data/data/patients/use-patient-item';
import {
  AgendaAppointmentInput,
  AllPracticesId,
  AppointmentModalData,
} from '../../../../data/data/types';
import { getConversationType } from '../../../../data/data/utils';
import {
  AgendaAppointmentItemDataFragment,
  AgendaAvailableSlotItemDataFragment,
  AgendaSlotItemDataFragment,
  BaseHealthProfessionalItemFragment,
  BulkCampaignType,
  ConsultType,
  ConversationItemDataFragment,
  Honorific,
  MutateAgendaAppointmentItemDocument,
  TemplateFrequency,
} from '../../../../data/types/api/graphql';
import {
  copyTimeFromDate,
  DateTimeFormat,
  formatDate,
  formatTime,
} from '../../../../data/utils/date.utils';
import {
  isEmptyOrNone,
  useFormatters,
} from '../../../../data/utils/formatters.utils';
import { useNavigateWithHealthProvider } from '../../../../data/utils/router.utils';
import { AlertMessageType } from '../../../../elements/alert-message/alert-message';
import {
  Button,
  ButtonIcon,
  ButtonType,
} from '../../../../elements/button/button';
import { InputCheckboxController } from '../../../../elements/form/controllers/input-checkbox';
import { InputFieldController } from '../../../../elements/form/controllers/input-field';
import { InputSelectController } from '../../../../elements/form/controllers/input-select';
import { InputSwitchController } from '../../../../elements/form/controllers/input-switch';
import { InputTextAreaController } from '../../../../elements/form/controllers/input-textarea';
import {
  FormColumns,
  FormRow,
  FormSection,
} from '../../../../elements/form/form';
import { BaseInputIcon } from '../../../../elements/form/input/base-input';
import { InputCheckboxType } from '../../../../elements/form/input/input-checkbox';
import { InputField } from '../../../../elements/form/input/input-field';
import { InputFieldPager } from '../../../../elements/form/input-field-pager';
import { CustomFormattedDate } from '../../../../elements/intl/custom-formatted-date';
import { CustomFormattedMessage } from '../../../../elements/intl/custom-formatted-message';
import { Message, MessageType } from '../../../../elements/message/message';
import { Modal, ModalWidth } from '../../../../elements/modal/modal';
import { ModalPrompt } from '../../../../elements/modal/modal-prompt';
import { NoteTag, NoteTagType } from '../../../../elements/note-tag/note-tag';
import { Tabs } from '../../../../elements/tabs/tabs';
import { CopyText } from '../../../../elements/utils/copy-text/copy-text';
import { InputAutoCompletePatientFieldController } from '../../../form/controllers/input-auto-complete-patient-field-controller';
import { AgendaAppointmentDescription } from '../../agenda-appointment-description';
import { AgendaAvailableBlockList } from '../../list/agenda-available-block-list';
import { AgendaAppointmentDeletePrompt } from '../agenda-appointment-delete-prompt/agenda-appointment-delete-prompt';
import { useAgendaAppointmentForm } from './form/use-agenda-appointment-form';

export const getAgendaAppointmentAppointmentInput = (
  value?: AgendaAppointmentItemDataFragment | null
): AgendaAppointmentInput => ({
  id: value?.id,
  title: value?.conversation?.title ?? value?.title,
  note: value?.note,
  startTime: value?.startTime,
  endTime: value?.endTime,
  // bulkId: value?.bulkSlotInfo?.bulkCampaignId,
  healthProfessional: value?.healthProfessional
    ? {
        id: value?.healthProfessional.id,
      }
    : null,
  patient: value?.patient
    ? {
        id: value.patient.id,
      }
    : null,
  conversation: value?.conversation
    ? {
        id: value.conversation.id,
      }
    : null,
  consultType: value?.consultType ?? ConsultType.Physical,
  honorific: value?.healthProfessional?.honorific,
  disableHonorific: false,
  recurring: value?.recurring,
});

export const getAgendaSlotAppointmentInput = (
  value: AgendaSlotItemDataFragment | null,
  healthProfessionalId: string | null | undefined = null,
  consultType: ConsultType | null = null
): AgendaAppointmentInput => ({
  startTime: value?.startTime,
  endTime: value?.endTime,
  bulkId: value?.bulkSlotInfo?.bulkCampaignId,
  title: value?.bulkSlotInfo?.title ?? null,
  healthProfessional: healthProfessionalId
    ? {
        id: healthProfessionalId,
      }
    : null,
  consultType: consultType ?? ConsultType.Physical,
  disableHonorific: true,
  irregular: value?.irregular,
});

const getFormStartStep = (value?: AgendaAppointmentInput) => {
  if (
    value?.consultType &&
    [ConsultType.Else, ConsultType.Exception].includes(value?.consultType)
  ) {
    return 1;
  }
  if (value?.id ?? value?.conversation?.id) {
    return 2;
  }

  return 1;
};

export function AgendaAppointmentModal({
  healthProviderId,
  agendaAppointmentItem,
  onSaved,
  onCancel,
  onClose,
  ...props
}: AppointmentModalData) {
  const intl = useIntl();

  const { showAlertMessage } = useAppContext();

  const formStartStep = getFormStartStep(agendaAppointmentItem);

  const [selectedSlots, setSelectedSlots] = useState<
    AgendaAvailableSlotItemDataFragment[]
  >([]);

  const [action, setAction] = useState<AgendaAppointmentModalAction>(
    AgendaAppointmentModalAction.DEFAULT
  );
  const [formStep, setFormStep] = useState<number>(1);

  useEffect(() => {
    setAction(props.action ?? AgendaAppointmentModalAction.DEFAULT);
  }, [props.action]);

  useEffect(() => {
    setFormStep(formStartStep);
  }, [action, formStartStep]);

  const onHandleFormStep = useCallback((nextStep: number) => {
    setFormStep(nextStep);
  }, []);

  const { form, formIsValid } = useAgendaAppointmentForm();

  const resetFields = useCallback(() => {
    if (agendaAppointmentItem) {
      console.log('APPOINTMENT DATA', agendaAppointmentItem);
      form.reset({
        ...form.getValues(),
        ...agendaAppointmentItem,
        title: agendaAppointmentItem.title ?? '',
        note: agendaAppointmentItem.note ?? '',
        healthProfessionalId: agendaAppointmentItem.healthProfessional?.id,
        bulkId: agendaAppointmentItem.bulkId ?? '', // TODO
        patientId: agendaAppointmentItem.patient?.id,
        conversationId: agendaAppointmentItem.conversation?.id,
        irregular: agendaAppointmentItem.irregular ?? false,
        consultType:
          agendaAppointmentItem.consultType === ConsultType.Exception
            ? ConsultType.Else
            : agendaAppointmentItem.consultType, // consulttype exception is consult type else
        conversationType: getConversationType(agendaAppointmentItem),
        startDate: agendaAppointmentItem?.startTime
          ? formatDate(agendaAppointmentItem?.startTime, DateTimeFormat)
          : '',
        endDate: agendaAppointmentItem?.endTime
          ? formatDate(agendaAppointmentItem?.endTime, DateTimeFormat)
          : '',
        rangeStartDate: agendaAppointmentItem?.startTime
          ? formatDate(agendaAppointmentItem?.startTime)
          : formatDate(new Date()),
        rangeEndDate: agendaAppointmentItem?.endTime
          ? formatDate(agendaAppointmentItem?.endTime)
          : formatDate(new Date()),
      });
    }
  }, [agendaAppointmentItem, form]);

  useEffect(() => {
    resetFields();
  }, [resetFields]);

  const { data: appointmentTypeData } = useAgendaAppointmentTypeValues();
  const { data: templateFrequencyData } = useAgendaTemplateFrequencyValues();
  const { data: conversationSelectTypeData } =
    useConversationSelectTypeValues();

  const consultType = form.watch('consultType');
  const patientId = form.watch('patientId');
  const bulkId = form.watch('bulkId');
  const patientsInAllPractices = form.watch('patientsInAllPractices');
  const conversationType = form.watch('conversationType');
  const conversationId = form.watch('conversationId');
  const conversationTitle = form.watch('conversationTitle');
  const honorific = form.watch('honorific');
  const healthProfessionalId = form.watch('healthProfessionalId');
  const title = form.watch('title');
  const frequency = form.watch('frequency');
  const startDate = form.watch('startDate');
  const endDate = form.watch('endDate');
  const note = form.watch('note');
  const rangeStartDate = form.watch('rangeStartDate');
  const rangeEndDate = form.watch('rangeEndDate');
  const minutes = form.watch('minutes');
  const irregular = form.watch('irregular') === true;

  const previousHonorific = usePrevious(honorific);
  const previousPatientId = usePrevious(patientId);

  const { data: noteEditors } = useAgendaAppointmentNotes({
    appointmentId: agendaAppointmentItem?.id,
  });

  const {
    data: healthProfessionalItemsData,
    loading: healthProfessionalItemsLoading,
  } = useHealthProfessionalItems(
    {
      healthProviderId,
      onlyAgenda: true,
    },
    action === AgendaAppointmentModalAction.DEFAULT ||
      ![ConsultType.Else, ConsultType.Exception].includes(consultType)
  );

  const healthProfessionalItems = useMemo(
    () => healthProfessionalItemsData?.items ?? [],
    [healthProfessionalItemsData]
  );

  const isInPast = useMemo(() => {
    if (agendaAppointmentItem?.id && agendaAppointmentItem?.startTime) {
      const appointmentDate = new Date(agendaAppointmentItem.startTime);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return appointmentDate < today;
    }
    return false;
  }, [agendaAppointmentItem]);

  const { data: patientItemData } = usePatientItem(patientId);

  const { data: healthProfessionalItemData } =
    useHealthProfessionalItem(healthProfessionalId);

  const {
    data: patientConversationItemsData,
    loading: patientConversationItemsLoading,
  } = usePatientConversationItems(
    {
      healthProviderId,
      patientId,
    },
    action === AgendaAppointmentModalAction.DEFAULT
  );

  const { patientConversationItem, patientConversationItems } = useMemo(() => {
    let conversationItem: ConversationItemDataFragment | null = null;

    if (conversationType === ConversationSelectType.EXISTING) {
      const patientConversationItemData = patientConversationItemsData.find(
        option => option.id === conversationId
      );

      conversationItem = {
        id: patientConversationItemData?.id ?? '',
        title: patientConversationItemData?.title ?? '',
      } as ConversationItemDataFragment;
    }

    if (conversationType === ConversationSelectType.NEW) {
      conversationItem = {
        title: conversationTitle,
      } as ConversationItemDataFragment;
    }

    return {
      patientConversationItem: conversationItem,
      patientConversationItems: patientConversationItemsData,
    };
  }, [
    conversationType,
    conversationId,
    conversationTitle,
    patientConversationItemsData,
  ]);

  // on patient change check converstion type
  useEffect(() => {
    if (
      !agendaAppointmentItem?.id &&
      !agendaAppointmentItem?.conversation?.id &&
      (!previousPatientId || previousPatientId !== patientId) &&
      !patientConversationItemsLoading
    ) {
      form.setValue(
        'conversationType',
        patientConversationItemsData.length > 0
          ? ConversationSelectType.EXISTING
          : ConversationSelectType.NEW
      );
    }
  }, [
    agendaAppointmentItem?.conversation?.id,
    agendaAppointmentItem?.id,
    form,
    patientConversationItemsData.length,
    patientConversationItemsLoading,
    patientId,
    previousPatientId,
  ]);

  // update on switch type
  useEffect(() => {
    if (
      !agendaAppointmentItem?.id &&
      !agendaAppointmentItem?.conversation?.id
    ) {
      if (conversationType !== ConversationSelectType.EXISTING) {
        form.setValue('conversationId', '', {
          shouldDirty: true,
          shouldValidate: true,
        });
      }
      if (conversationType !== ConversationSelectType.NEW) {
        form.setValue('conversationTitle', '', {
          shouldDirty: true,
          shouldValidate: true,
        });
      }
    }
  }, [
    agendaAppointmentItem?.conversation?.id,
    agendaAppointmentItem?.id,
    conversationType,
    form,
  ]);

  // get bulk campains if any show bulk in available types
  const { data: bulkMessageItemsData, loading: bulkMessageItemsDataLoading } =
    useAvailableBulkMessageItems({
      healthProviderId,
      type: BulkCampaignType.BulkInvite,
    });

  const bulkItems = useMemo(() => {
    if (bulkMessageItemsDataLoading) {
      return [];
    }
    return (
      bulkMessageItemsData?.items?.map(x => ({
        key: x.id,
        title: x.internalTitle,
      })) ?? []
    );
  }, [bulkMessageItemsData?.items, bulkMessageItemsDataLoading]);

  const onHandleBulkCampaign = useCallback(
    (id: string | null) => {
      if (id && bulkId !== id) {
        form.setValue('bulkId', id, {
          shouldDirty: true,
          shouldValidate: true,
        });
      }
    },
    [form, bulkId]
  );

  const { isMultiple, availableAppointmentTypes } = useMemo(() => {
    let availableTypes: { key: ConsultType; title: string }[] = [];

    // if loading skip
    if (bulkMessageItemsDataLoading) {
      return {
        isMultiple: frequency !== TemplateFrequency.Once,
        availableAppointmentTypes: availableTypes,
      };
    }

    // filter out if appointment type or conversationId is set (else => consulttype is accepted consulttype => else not )
    if (agendaAppointmentItem?.conversation?.id) {
      availableTypes = appointmentTypeData.filter(
        x => x.key !== ConsultType.Exception && x.key !== ConsultType.Else
      );
    } else {
      availableTypes = appointmentTypeData;
    }

    const bulkType = availableTypes.find(type => type.key === ConsultType.Bulk);
    if (bulkType) {
      availableTypes = [
        ...availableTypes.filter(type => type.key !== ConsultType.Bulk),
        bulkType,
      ];
    }

    // all types disabled if bulk appointment
    if (agendaAppointmentItem?.bulkId) {
      availableTypes = [];
    }

    // if id disable switching to bulk
    if (agendaAppointmentItem?.id) {
      availableTypes = [
        ...availableTypes.filter(type => type.key !== ConsultType.Bulk),
      ];
    }

    return {
      isMultiple: frequency !== TemplateFrequency.Once,
      availableAppointmentTypes: availableTypes,
    };
  }, [
    bulkMessageItemsDataLoading,
    agendaAppointmentItem?.conversation?.id,
    agendaAppointmentItem?.bulkId,
    agendaAppointmentItem?.id,
    frequency,
    appointmentTypeData,
  ]);

  // set date
  const [date, setDate] = useState<string>(formatDate(new Date()));

  useEffect(() => {
    if (agendaAppointmentItem?.startTime) {
      setDate(formatDate(agendaAppointmentItem.startTime));
    }
  }, [agendaAppointmentItem]);

  // previous / next dates
  const { prevDate, nextDate } = useMemo(
    () => ({
      prevDate:
        date &&
        new Date().setHours(0, 0, 0, 0) < new Date(date).setHours(0, 0, 0, 0),
      nextDate: date,
    }),
    [date]
  );

  const selectedHealthProfessional = useMemo(() => {
    if (agendaAppointmentItem?.healthProfessional?.id) {
      return [agendaAppointmentItem?.healthProfessional?.id];
    }
    if (healthProfessionalId) {
      return [healthProfessionalId];
    }
    return null;
  }, [agendaAppointmentItem?.healthProfessional?.id, healthProfessionalId]);

  const healtProfessionalToInclude = useMemo(() => {
    // Else / Exception need original healthprofessional!
    if (
      [ConsultType.Else, ConsultType.Exception].includes(consultType) &&
      healthProfessionalId
    ) {
      return [healthProfessionalId];
    }
    // if update show all healthprofessionals
    if (action === AgendaAppointmentModalAction.UPDATE) {
      return null;
    }

    // show specific healthprofessional
    if (agendaAppointmentItem?.healthProfessional?.id) {
      return [agendaAppointmentItem?.healthProfessional?.id];
    }

    return null;
  }, [
    action,
    agendaAppointmentItem?.healthProfessional?.id,
    consultType,
    healthProfessionalId,
  ]);

  const { currentStartDate, currentEndDate } = useMemo(() => {
    let end = date;
    let start = date;
    if ([ConsultType.Else, ConsultType.Exception].includes(consultType)) {
      end = frequency === TemplateFrequency.Once ? date : rangeEndDate;
      start = rangeStartDate;
    }
    return {
      currentStartDate: start,
      currentEndDate: end,
    };
  }, [consultType, date, frequency, rangeEndDate, rangeStartDate]);

  // get honorific and time data
  const {
    data: availableHonorificData,
    loading: availableHonorificDataLoading,
  } = useAvailableAppointmentHonorificBlockMinuteValues({
    healthProviderId:
      action === AgendaAppointmentModalAction.DEFAULT ? null : healthProviderId,
    internalAgenda: true,
    healthProfessionalId: null, // always show all available honorifics
    bulkId,
    consultTypes: [consultType],
  });

  const honorifics = useMemo(
    () => availableHonorificData.map(x => ({ key: x.key, title: x.title })),
    [availableHonorificData]
  );

  // get blockTimes and defaultBlockTime
  const { blockTimes, defaultBlockTime } = useMemo(() => {
    if (availableHonorificDataLoading) {
      return {
        blockTimes: [],
        defaultBlockTime: 0,
      };
    }

    const item = availableHonorificData.find(x => x.key === honorific);

    const times =
      item?.values?.map(value => ({
        key: Number(value.key),
        title: value.title,
        irregular: value.irregular,
      })) ?? [];

    let calculatedDefaultTime = [ConsultType.Bulk].includes(consultType)
      ? null
      : (item?.defaultValue ?? null);

    // calculate closest
    // dont calculate if bulk
    if (
      ![ConsultType.Bulk].includes(consultType) &&
      startDate &&
      endDate &&
      times.length
    ) {
      const appointmentLength = Math.round(
        (new Date(endDate).getTime() - new Date(startDate).getTime()) / 60000
      );
      console.log('appointmentLength', appointmentLength);
      if (appointmentLength > 0) {
        calculatedDefaultTime = times.reduce((closest, curr) =>
          Math.abs(curr.key - appointmentLength) <
          Math.abs(closest.key - appointmentLength)
            ? curr
            : closest
        ).key;

        // Mark as calculated for this honorific
      }
    }

    return {
      blockTimes: times,
      defaultBlockTime: calculatedDefaultTime,
    };
  }, [
    availableHonorificData,
    availableHonorificDataLoading,
    consultType,
    endDate,
    honorific,
    startDate,
  ]);

  const filteredBlockTimes = useMemo(
    () =>
      irregular ? blockTimes : blockTimes.filter(x => x.irregular === false),
    [blockTimes, irregular]
  );

  useEffect(() => {
    const t = selectedSlots.map(slot => ({
      start: formatTime(slot.startTime),
      end: formatTime(slot.endTime),
    }));
    console.log('selected slots', t);
  }, [selectedSlots]);

  // auto set minutes based on honorific change
  const minutesSet = useRef(false);
  useEffect(() => {
    if (
      !availableHonorificDataLoading &&
      honorific &&
      honorific !== previousHonorific &&
      defaultBlockTime &&
      minutesSet.current === false
    ) {
      console.log('set minutes', defaultBlockTime);
      form.setValue('minutes', defaultBlockTime);
      minutesSet.current = true;
    }
  }, [
    availableHonorificDataLoading,
    defaultBlockTime,
    form,
    honorific,
    previousHonorific,
  ]);

  const selectedHonorifics = useMemo(() => {
    if (
      consultType === ConsultType.Bulk &&
      !agendaAppointmentItem?.healthProfessional?.id
    ) {
      return null;
    }
    return honorific ? [honorific] : null;
  }, [agendaAppointmentItem?.healthProfessional?.id, consultType, honorific]);

  // get available items
  // else needs healthprofessional to work
  const { data: blocks, loading: blocksLoading } = useAgendaAvailableItems(
    {
      healthProviderId,
      startDate: currentStartDate,
      irregular: ![ConsultType.Exception, ConsultType.Telephone].includes(
        consultType
      )
        ? irregular
        : false,
      endDate: currentEndDate,
      consultType,
      honorifics: selectedHonorifics,
      frequency,
      healthProfessionalId: healtProfessionalToInclude,
      minuteLength: ![ConsultType.Exception, ConsultType.Telephone].includes(
        consultType
      )
        ? minutes
        : null,
      currentAppointmentId: agendaAppointmentItem?.id ?? null,
      bulkCampaignId: !isEmpty(bulkId) ? bulkId : null,
    },
    action === AgendaAppointmentModalAction.DEFAULT ||
      ([ConsultType.Else, ConsultType.Exception].includes(consultType) &&
        selectedHealthProfessional === null) ||
      formStep !== 2
  );

  // first available item
  const { data: firstAvailableDates } = useAgendaFirstAvailableItem(
    {
      healthProviderId,
      consultType,
      honorific: selectedHonorifics,
      healthProfessionalId: selectedHealthProfessional,
      bulkCampaignId: !isEmpty(bulkId) ? bulkId : null,
      minutes: ![ConsultType.Exception, ConsultType.Telephone].includes(
        consultType
      )
        ? minutes
        : null,
    },
    ((previousHonorific && honorific && honorific === previousHonorific) ??
      (blocksLoading === true && blocks.length > 0)) ||
      action === AgendaAppointmentModalAction.DEFAULT ||
      [ConsultType.Else, ConsultType.Exception].includes(consultType) ||
      formStep !== 2
  );

  const { next } = useMemo(() => {
    if (
      !blocksLoading &&
      blocks.length === 0 &&
      firstAvailableDates &&
      firstAvailableDates?.length > 0 &&
      date
    ) {
      const now = formatDate(new Date(date));
      return {
        previous: last(firstAvailableDates?.filter(d => d < now)) ?? null,
        next: first(firstAvailableDates?.filter(d => d > now)) ?? null,
      };
    }
    return { previous: null, next: null };
  }, [blocks.length, blocksLoading, date, firstAvailableDates]);

  // auto select slots
  useEffect(() => {
    if (
      !availableHonorificDataLoading &&
      !blocksLoading &&
      agendaAppointmentItem &&
      (agendaAppointmentItem?.startTime !== null ||
        agendaAppointmentItem?.startTime !== undefined) &&
      (agendaAppointmentItem?.endTime !== null ||
        agendaAppointmentItem?.endTime !== undefined)
    ) {
      const filterHealthProfessional = blocks.find(
        x => x.healthProfessional.id === healthProfessionalId
      );
      if (
        filterHealthProfessional &&
        agendaAppointmentItem.startTime &&
        agendaAppointmentItem.endTime
      ) {
        const start = new Date(agendaAppointmentItem?.startTime);
        const end = new Date(agendaAppointmentItem?.endTime);

        const selected = filterHealthProfessional.slots.filter(slot => {
          const slotEnd = new Date(slot.endTime);
          const slotStart = new Date(slot.startTime);
          if (
            slotEnd.getTime() <= end.getTime() &&
            slotStart.getTime() >= start.getTime()
          ) {
            return slot;
          }

          return undefined;
        });

        const slots = selected as AgendaAvailableSlotItemDataFragment[];

        let filteredSlots: AgendaAvailableSlotItemDataFragment[] = [];

        // only select one slot if practice / video
        if (slots.length > 0) {
          filteredSlots = [
            ConsultType.Else,
            ConsultType.Exception,
            ConsultType.Visit,
          ].includes(consultType)
            ? slots
            : [slots[0]];
        }

        setSelectedSlots(filteredSlots);
      }
    }
  }, [
    agendaAppointmentItem,
    availableHonorificDataLoading,
    blocks,
    blocksLoading,
    consultType,
    healthProfessionalId,
  ]);

  // clear values on switch consultType
  const onHandleAppointmentType = useCallback(
    (value: ConsultType) => {
      form.setValue('consultType', value, {
        shouldDirty: true,
        shouldValidate: true,
      });

      if (
        !agendaAppointmentItem?.id &&
        !agendaAppointmentItem?.conversation?.id
      ) {
        if (value === ConsultType.Else) {
          form.setValue('patientId', '');
          form.setValue('conversationTitle', '');
          form.setValue('conversationId', '');
        } else {
          form.setValue('title', '');
          form.setValue('frequency', TemplateFrequency.Once);
          form.setValue(
            'healthProfessionalId',
            agendaAppointmentItem?.healthProfessional?.id ?? ''
          );
          setSelectedSlots([]);
        }

        form.setValue(
          'startDate',

          formatDate(
            agendaAppointmentItem?.startTime ?? new Date(),
            DateTimeFormat
          ),
          { shouldValidate: true }
        );
        form.setValue(
          'endDate',

          formatDate(
            agendaAppointmentItem?.endTime ?? new Date(),
            DateTimeFormat
          ),
          { shouldValidate: true }
        );
        form.setValue(
          'rangeStartDate',

          formatDate(agendaAppointmentItem?.startTime ?? new Date()),
          { shouldValidate: true }
        );
        form.setValue(
          'rangeEndDate',

          formatDate(agendaAppointmentItem?.startTime ?? new Date()),
          { shouldValidate: true }
        );
      }
    },
    [
      form,
      agendaAppointmentItem?.id,
      agendaAppointmentItem?.conversation?.id,
      agendaAppointmentItem?.startTime,
      agendaAppointmentItem?.endTime,
      agendaAppointmentItem?.healthProfessional?.id,
    ]
  );

  // handle range date
  // convert range date to datetime if startdate has time
  const onHandleDateRangeStart = useCallback(
    (value: string) => {
      setSelectedSlots([]);
      const dateTime = copyTimeFromDate(
        startDate ? new Date(startDate) : new Date(),
        new Date(value)
      );
      form.setValue('startDate', dateTime, { shouldValidate: true });
      // update  enddate
      if (new Date(value).getTime() > new Date(rangeEndDate).getTime()) {
        form.setValue('rangeEndDate', formatDate(dateTime), {
          shouldValidate: true,
        });
      }
    },
    [form, rangeEndDate, startDate]
  );

  const onHandleDateRangeEnd = useCallback(
    (value: string) => {
      setSelectedSlots([]);
      const dateTime = copyTimeFromDate(
        endDate ? new Date(endDate) : new Date(),
        new Date(value)
      );
      form.setValue('endDate', dateTime, { shouldValidate: true });
    },
    [endDate, form]
  );

  // handle single date
  const onHandleDate = useCallback((value: string) => {
    setDate(value);
  }, []);

  const onHandleDatePager = useCallback(
    (type: 'prev' | 'next') => {
      const currentDate = new Date(date);

      if (type === 'prev') {
        currentDate.setDate(currentDate.getDate() - 1);
      }

      if (type === 'next') {
        currentDate.setDate(currentDate.getDate() + 1);
      }

      setDate(formatDate(currentDate));
    },
    [date]
  );

  const onHandleSlots = useCallback(
    (
      healthProfessional: BaseHealthProfessionalItemFragment,
      slots: AgendaAvailableSlotItemDataFragment[]
    ) => {
      if (healthProfessionalId !== healthProfessional.id) {
        form.setValue('healthProfessionalId', healthProfessional.id, {
          shouldDirty: true,
          shouldValidate: true,
        });
      }

      console.log('handle slots', slots);

      setSelectedSlots(slots);

      if (slots.length > 0) {
        const start = slots?.[0].startTime;
        const end = slots[slots.length - 1]?.endTime;

        if (startDate !== start) {
          form.setValue('startDate', start, {
            shouldDirty: true,
            shouldValidate: true,
          });
        }

        if (endDate !== end) {
          form.setValue('endDate', end, {
            shouldDirty: true,
            shouldValidate: true,
          });
        }
      }
    },

    [endDate, form, healthProfessionalId, startDate]
  );

  const onHandleHealthProfessional = useCallback(
    (id: string | null) => {
      if (id && healthProfessionalId !== id) {
        form.setValue('healthProfessionalId', id, {
          shouldDirty: true,
          shouldValidate: true,
        });
      }
    },
    [form, healthProfessionalId]
  );

  // set honorific to healthprofessional if no honorific available
  useEffect(() => {
    if (
      !agendaAppointmentItem?.honorific &&
      healthProfessionalItemData?.honorific
    ) {
      form.setValue('honorific', healthProfessionalItemData?.honorific, {
        shouldValidate: true,
      });
    }
  }, [
    agendaAppointmentItem?.honorific,
    form,
    healthProfessionalItemData,
    honorific,
  ]);

  // handle endDate
  useEffect(() => {
    if ([ConsultType.Else, ConsultType.Exception].includes(consultType)) {
      const end = new Date(endDate).getTime();
      const start = new Date(startDate).getTime();
      if (end < start) {
        form.setValue(
          'endDate',

          startDate,
          { shouldValidate: true }
        );
      }
    }
  }, [consultType, endDate, form, startDate]);

  // honorific changes
  useEffect(() => {
    if (
      previousHonorific &&
      honorific !== previousHonorific &&
      !agendaAppointmentItem?.disableHonorific
    ) {
      form.setValue('healthProfessionalId', '', {
        shouldDirty: true,
        shouldValidate: true,
      });

      form.setValue('startDate', '', {
        shouldDirty: true,
        shouldValidate: true,
      });

      form.setValue('endDate', '', {
        shouldDirty: true,
        shouldValidate: true,
      });

      form.setValue('honorific', honorific, {
        shouldValidate: true,
      });

      // reset minuteSet
      minutesSet.current = false;
    }
  }, [
    agendaAppointmentItem?.disableHonorific,
    form,
    honorific,
    previousHonorific,
  ]);

  const getNextStep = useMemo(() => formStep + 1, [formStep]);

  const getPreviousStep = useMemo(() => formStep - 1, [formStep]);

  const { range, firstInRange, lastInRange } = useMemo(() => {
    const all = selectedSlots.map(item => ({
      startDateTime: formatDate(item.startTime, DateTimeFormat),
      endDateTime: formatDate(item.endTime, DateTimeFormat),
    }));

    const lastItem = all[all.length - 1];
    const firstItem = all[0];

    return { range: all, firstInRange: firstItem, lastInRange: lastItem };
  }, [selectedSlots]);

  const canDoNext = useMemo(() => {
    console.log('Step', formStep);

    // STEP 1
    // else / exception
    if (formStep === 1) {
      if ([ConsultType.Else, ConsultType.Exception].includes(consultType)) {
        return title && healthProfessionalId;
      }

      // bulk
      if ([ConsultType.Bulk].includes(consultType)) {
        return bulkId && patientId;
      }

      // common
      if (
        [
          ConsultType.Physical,
          ConsultType.Video,
          ConsultType.Visit,
          ConsultType.Telephone,
        ].includes(consultType)
      ) {
        if (formStep === 1 && patientId) {
          // needs id
          if (
            conversationType === ConversationSelectType.EXISTING &&
            conversationId
          ) {
            return true;
          }
          // needs title
          if (
            conversationType === ConversationSelectType.NEW &&
            conversationTitle
          ) {
            return true;
          }
        }
      }
    }

    // STEP 2

    if (formStep === 2) {
      console.log('step 2  consult type', consultType);

      if ([ConsultType.Else, ConsultType.Exception].includes(consultType)) {
        if (frequency === TemplateFrequency.Once && range.length > 0) {
          return true;
        }
        if (
          ![TemplateFrequency.Once].includes(frequency) &&
          startDate &&
          endDate &&
          selectedSlots.length > 0
        ) {
          return true;
        }
      }

      if (
        [
          ConsultType.Physical,
          ConsultType.Video,
          ConsultType.Visit,
          ConsultType.Telephone,
          ConsultType.Bulk,
        ].includes(consultType) &&
        selectedSlots.length > 0 &&
        blocks.length > 0
      ) {
        console.log('selected slots', selectedSlots, 'blocks', blocks);
        return true;
      }
    }

    // STEP 3
    if (formStep === 3) {
      return true;
    }

    // STEP 4
    if (formStep === 4) {
      console.log('is valid', formIsValid);

      return formIsValid;
    }

    return false;
  }, [
    formStep,
    consultType,
    title,
    healthProfessionalId,
    bulkId,
    patientId,
    conversationType,
    conversationId,
    conversationTitle,
    selectedSlots,
    blocks,
    frequency,
    range.length,
    startDate,
    endDate,
    formIsValid,
  ]);

  const navigate = useNavigateWithHealthProvider();

  const onHandleChat = useCallback(() => {
    navigate(`/conversations/${agendaAppointmentItem?.conversation?.id}`);
  }, [agendaAppointmentItem, navigate]);

  const [
    mutateAgendaAppointmentItem,
    { loading: mutateAgendaAppointmentItemLoading },
  ] = useMutation(MutateAgendaAppointmentItemDocument);

  const onHandleSubmit = useCallback(() => {
    form.handleSubmit(async () => {
      const values = form.getValues();
      console.log(range);

      try {
        const result = await mutateAgendaAppointmentItem({
          variables: {
            ...values,
            id: agendaAppointmentItem?.id ?? null,
            healthProviderId,
            bulkId: !isEmpty(values.bulkId) ? values.bulkId : null,
            patientId: !isEmpty(values.patientId) ? values.patientId : null,
            conversationId:
              ![
                ConsultType.Else,
                ConsultType.Exception,
                ConsultType.Bulk,
              ].includes(values.consultType) &&
              values.conversationType === ConversationSelectType.EXISTING
                ? values.conversationId
                : null,
            conversationTitle:
              ![
                ConsultType.Else,
                ConsultType.Exception,
                ConsultType.Bulk,
              ].includes(values.consultType) &&
              values.conversationType === ConversationSelectType.NEW
                ? values.conversationTitle
                : null,
            consultType: values.consultType as unknown as ConsultType,
            startDateTime: range[0].startDateTime,
            endDateTime: range[range.length - 1]!.endDateTime,
          },
        });

        if (result.data?.appointmentData.appointment) {
          showAlertMessage({
            type: AlertMessageType.SUCCESS,
            message: intl.formatMessage({
              id: 'agenda.appointment-create.success',
            }),
          });

          onSaved?.();

          onClose?.();
        } else {
          throw new Error(
            'An unknown error has occurred in the server respond.'
          );
        }
      } catch (error) {
        showAlertMessage({
          type: AlertMessageType.ERROR,
          message: (error as Error).message,
        });
      }
    })();
  }, [
    agendaAppointmentItem?.id,
    form,
    healthProviderId,
    intl,
    mutateAgendaAppointmentItem,
    onClose,
    onSaved,
    range,
    showAlertMessage,
  ]);

  const [cancelPromptOpen, setCancelPromptOpen] = useState<boolean>(false);

  const onHandleCancel = useCallback(
    (confirmed: boolean = false) => {
      if (action !== AgendaAppointmentModalAction.DEFAULT) {
        if (!confirmed && form.formState.isDirty) {
          setCancelPromptOpen(true);
        } else if (agendaAppointmentItem?.id) {
          resetFields();
          setAction(AgendaAppointmentModalAction.DEFAULT);
        } else {
          onCancel?.();

          onClose?.();
        }
      } else {
        onCancel?.();

        onClose?.();
      }
    },
    [
      action,
      form.formState.isDirty,
      agendaAppointmentItem?.id,
      resetFields,
      onCancel,
      onClose,
    ]
  );

  const [deleteModalOpen, setDeleteModalOpen] = useState<boolean>(false);

  const { getFullName, getBirthDate } = useFormatters();

  return (
    <>
      <Modal
        testId={`${kebabCase(action)}-appointment`}
        title={
          action !== AgendaAppointmentModalAction.DEFAULT ? (
            <CustomFormattedMessage
              id={`agenda.appointment-${kebabCase(action)}.title`}
            />
          ) : (
            <CustomFormattedMessage id="agenda.appointment.title" />
          )
        }
        width={ModalWidth.LARGE}
        onClose={onHandleCancel}
        buttons={
          <>
            {action !== AgendaAppointmentModalAction.DEFAULT && (
              <>
                <Button
                  type={ButtonType.SECONDARY}
                  inverted
                  onClick={() => onHandleCancel(false)}
                >
                  <CustomFormattedMessage id="common.cancel" />
                </Button>

                {formStep > formStartStep && (
                  <Button
                    inverted
                    onClick={() => onHandleFormStep(getPreviousStep)}
                  >
                    <CustomFormattedMessage id="common.previous" />
                  </Button>
                )}

                {formStep < 4 && (
                  <Button
                    icon={
                      bulkMessageItemsDataLoading
                        ? ButtonIcon.LOADING
                        : ButtonIcon.NONE
                    }
                    onClick={() => onHandleFormStep(getNextStep)}
                    disabled={!canDoNext}
                  >
                    <CustomFormattedMessage id="common.next" />
                  </Button>
                )}

                {formStep === 4 && (
                  <Button
                    icon={
                      mutateAgendaAppointmentItemLoading
                        ? ButtonIcon.LOADING
                        : ButtonIcon.NONE
                    }
                    disabled={
                      !formIsValid ||
                      mutateAgendaAppointmentItemLoading ||
                      range.length === 0
                    }
                    onClick={onHandleSubmit}
                  >
                    {agendaAppointmentItem?.id ? (
                      <CustomFormattedMessage id="agenda.appointment-create.edit" />
                    ) : (
                      <CustomFormattedMessage id="agenda.appointment-create.save" />
                    )}
                  </Button>
                )}
              </>
            )}

            {action === AgendaAppointmentModalAction.DEFAULT && (
              <>
                <Button
                  type={ButtonType.SECONDARY}
                  inverted
                  disabled={isInPast}
                  onClick={() => setDeleteModalOpen(true)}
                >
                  <CustomFormattedMessage id="common.delete" />
                </Button>
                {!isInPast && (
                  <Button
                    icon={
                      bulkMessageItemsDataLoading
                        ? ButtonIcon.LOADING
                        : ButtonIcon.NONE
                    }
                    onClick={() =>
                      setAction(AgendaAppointmentModalAction.UPDATE)
                    }
                  >
                    <CustomFormattedMessage id="agenda.appointment.button.update" />
                  </Button>
                )}
              </>
            )}
          </>
        }
      >
        <FormProvider {...form}>
          {action === AgendaAppointmentModalAction.DEFAULT ? (
            <>
              <h4>
                <CustomFormattedMessage id="common.health-professional" />
              </h4>
              <p>
                {healthProfessionalItemData ? (
                  getFullName(healthProfessionalItemData)
                ) : (
                  <CustomFormattedMessage id="agenda.appointment.no-health-professional" />
                )}
              </p>

              {agendaAppointmentItem?.consultType &&
              ![ConsultType.Else, ConsultType.Exception].includes(
                agendaAppointmentItem.consultType
              ) ? (
                <>
                  <h4>
                    <CustomFormattedMessage id="common.patient" />
                  </h4>
                  <p>
                    {patientItemData ? (
                      <>
                        {getFullName(patientItemData)}
                        <br />
                        {!isEmptyOrNone(patientItemData?.birthDate) ? (
                          <CopyText
                            text={formatDate(
                              patientItemData.birthDate,
                              'dd-mm-YYYY'
                            )}
                          >
                            {getBirthDate(patientItemData)}
                          </CopyText>
                        ) : (
                          '-'
                        )}
                      </>
                    ) : (
                      <CustomFormattedMessage id="agenda.appointment.no-patient" />
                    )}
                  </p>

                  <h4>
                    <CustomFormattedMessage id="common.conversation.conversation-type" />
                  </h4>
                  <p>
                    {!isEmpty(agendaAppointmentItem?.conversation?.id) ? (
                      <>
                        {agendaAppointmentItem?.title}{' '}
                        <Button
                          type={ButtonType.TEXT}
                          icon={ButtonIcon.CHEVRON_RIGHT}
                          onClick={() => onHandleChat()}
                        >
                          <CustomFormattedMessage id="common.open-chat" />
                        </Button>
                      </>
                    ) : (
                      <CustomFormattedMessage id="agenda.appointment-create.no-conversation-type" />
                    )}
                  </p>
                </>
              ) : (
                <>
                  <h4>
                    <CustomFormattedMessage id="agenda.appointment-create.note" />
                  </h4>
                  <p>
                    {!isEmptyOrNone(agendaAppointmentItem?.title)
                      ? agendaAppointmentItem?.title
                      : '-'}
                  </p>
                </>
              )}
              <h4>
                {agendaAppointmentItem?.consultType &&
                ![ConsultType.Else, ConsultType.Exception].includes(
                  agendaAppointmentItem.consultType
                ) ? (
                  <CustomFormattedMessage id="agenda.appointment-create.note-patient" />
                ) : (
                  <CustomFormattedMessage id="common.note" />
                )}
                {noteEditors?.length > 0 && (
                  <div className="editors">
                    {noteEditors.map((editor, index) => (
                      <NoteTag
                        key={editor}
                        text={editor}
                        type={
                          index === 0 ? NoteTagType.CREATOR : NoteTagType.EDITOR
                        }
                      />
                    ))}
                  </div>
                )}
              </h4>
              <p>
                {!isEmptyOrNone(agendaAppointmentItem?.note)
                  ? agendaAppointmentItem?.note
                  : '-'}
              </p>

              <h4>
                <CustomFormattedMessage id="agenda.appointment-create.appointment" />
              </h4>
              <p>
                {!isEmpty(agendaAppointmentItem?.startTime) &&
                !isEmpty(agendaAppointmentItem?.endTime) ? (
                  <AgendaAppointmentDescription
                    data={{
                      startDate: agendaAppointmentItem?.startTime ?? '',
                      endDate: agendaAppointmentItem?.endTime ?? '',
                      frequency,
                      type: agendaAppointmentItem?.consultType,
                    }}
                  />
                ) : (
                  <CustomFormattedMessage id="agenda.appointment-create.no-date" />
                )}
              </p>

              {agendaAppointmentItem?.consultType === ConsultType.Video && (
                <>
                  <h4>
                    <CustomFormattedMessage id="common.video-link" />
                  </h4>
                  <p>
                    {healthProfessionalItemData?.meetingRoomLink &&
                    !isEmptyOrNone(
                      healthProfessionalItemData?.meetingRoomLink
                    ) ? (
                      <a
                        href={healthProfessionalItemData?.meetingRoomLink}
                        target="_blank"
                        title={healthProfessionalItemData?.meetingRoomLink}
                        rel="noreferrer"
                      >
                        {healthProfessionalItemData?.meetingRoomLink}
                      </a>
                    ) : (
                      '-'
                    )}
                  </p>
                </>
              )}
            </>
          ) : (
            <>
              {formStep === 1 && (
                <>
                  {formStartStep === 1 && (
                    <Tabs<ConsultType>
                      value={consultType}
                      items={availableAppointmentTypes}
                      onChange={onHandleAppointmentType}
                    />
                  )}

                  {consultType === ConsultType.Else && (
                    <FormRow>
                      <InputFieldController
                        name="title"
                        label={
                          <>
                            <CustomFormattedMessage id="agenda.appointment-create.description" />
                            <small>*</small>
                          </>
                        }
                        placeholder={intl.formatMessage({
                          id: 'agenda.appointment-create.description-placeholder',
                        })}
                      />
                      <InputSelectController<string>
                        name="healthProfessionalId"
                        label={
                          <>
                            <CustomFormattedMessage id="common.health-professional" />
                            <small>*</small>
                          </>
                        }
                        placeholder={intl.formatMessage({
                          id: 'common.health-professional-placeholder',
                        })}
                        disabled={
                          healthProfessionalItemsLoading ||
                          !!agendaAppointmentItem?.healthProfessional?.id
                        }
                        options={healthProfessionalItems.map(option => ({
                          ...option,
                          key: option.id,
                          title: getFullName(option),
                        }))}
                        value={healthProfessionalId}
                        onChange={option => {
                          onHandleHealthProfessional(option);
                        }}
                      />
                    </FormRow>
                  )}

                  {[ConsultType.Bulk].includes(consultType) && (
                    <>
                      <FormColumns>
                        <FormRow>
                          <InputAutoCompletePatientFieldController
                            name="patientId"
                            label={
                              <>
                                <CustomFormattedMessage id="common.patient" />
                                <small>*</small>
                              </>
                            }
                            placeholder={intl.formatMessage({
                              id: 'common.patients-search-placeholders',
                            })}
                            option={getFullName(patientItemData)}
                            optionVariables={{
                              healthProviderId: !patientsInAllPractices
                                ? healthProviderId
                                : AllPracticesId,
                            }}
                          />
                        </FormRow>

                        <FormRow width="150px">
                          <InputSwitchController
                            name="patientsInAllPractices"
                            label={
                              <CustomFormattedMessage id="common.all-pratices" />
                            }
                          />
                        </FormRow>
                      </FormColumns>
                      <FormSection>
                        {!patientId ? (
                          <p className="info">
                            <CustomFormattedMessage id="common.conversation.select-patient" />
                          </p>
                        ) : (
                          <FormRow>
                            <InputSelectController<string>
                              name="bulkId"
                              label={
                                <>
                                  <CustomFormattedMessage id="common.bulk-campaign" />
                                  <small>*</small>
                                </>
                              }
                              placeholder={intl.formatMessage({
                                id: 'common.bulk-campaign-placeholder',
                              })}
                              disabled={
                                bulkMessageItemsDataLoading ||
                                !!agendaAppointmentItem?.bulkId
                              }
                              options={bulkItems}
                              value={bulkId}
                              onChange={option => {
                                onHandleBulkCampaign(option);
                              }}
                            />
                          </FormRow>
                        )}
                      </FormSection>
                    </>
                  )}

                  {[
                    ConsultType.Visit,
                    ConsultType.Video,
                    ConsultType.Physical,
                    ConsultType.Telephone,
                  ].includes(consultType) && (
                    <>
                      <FormColumns>
                        <FormRow>
                          <InputAutoCompletePatientFieldController
                            name="patientId"
                            label={
                              <>
                                <CustomFormattedMessage id="common.patient" />
                                <small>*</small>
                              </>
                            }
                            placeholder={intl.formatMessage({
                              id: 'common.patients-search-placeholders',
                            })}
                            option={getFullName(patientItemData)}
                            optionVariables={{
                              healthProviderId: !patientsInAllPractices
                                ? healthProviderId
                                : AllPracticesId,
                            }}
                          />
                        </FormRow>

                        <FormRow width="150px">
                          <InputSwitchController
                            name="patientsInAllPractices"
                            label={
                              <CustomFormattedMessage id="common.all-pratices" />
                            }
                          />
                        </FormRow>
                      </FormColumns>

                      <FormSection>
                        {!patientId ? (
                          <p className="info">
                            <CustomFormattedMessage id="common.conversation.select-patient" />
                          </p>
                        ) : (
                          <>
                            <FormRow>
                              <InputCheckboxController<ConversationSelectType>
                                type={InputCheckboxType.RADIO}
                                name="conversationType"
                                label={
                                  <>
                                    <CustomFormattedMessage id="common.conversation.conversation-type" />
                                    <small>*</small>
                                  </>
                                }
                                options={conversationSelectTypeData}
                              />
                            </FormRow>

                            <hr />

                            {!conversationType ? (
                              <p className="info">
                                <CustomFormattedMessage id="common.conversation.select-conversation-type" />
                              </p>
                            ) : (
                              <>
                                {conversationType ===
                                  ConversationSelectType.EXISTING && (
                                  <FormRow>
                                    <InputCheckboxController
                                      type={InputCheckboxType.RADIO}
                                      name="conversationId"
                                      label={
                                        <>
                                          <CustomFormattedMessage id="common.conversation.existing-conversation" />
                                          <small>*</small>
                                        </>
                                      }
                                      placeholder={intl.formatMessage({
                                        id: 'common.conversation.no-existing-conversation',
                                      })}
                                      options={patientConversationItems.map(
                                        option => ({
                                          key: option.id,
                                          title: option.title,
                                        })
                                      )}
                                    />
                                  </FormRow>
                                )}

                                {conversationType ===
                                  ConversationSelectType.NEW && (
                                  <FormRow>
                                    <InputFieldController
                                      type="text"
                                      name="conversationTitle"
                                      label={
                                        <>
                                          <CustomFormattedMessage id="common.conversation.new-conversation" />
                                          <small>*</small>
                                        </>
                                      }
                                      placeholder={intl.formatMessage({
                                        id: 'common.conversation.new-conversation',
                                      })}
                                    />
                                  </FormRow>
                                )}
                              </>
                            )}
                          </>
                        )}
                      </FormSection>
                    </>
                  )}
                </>
              )}

              {formStep === 2 && !isInPast && (
                <>
                  {formStartStep === 2 && (
                    <Tabs<ConsultType>
                      value={consultType}
                      items={availableAppointmentTypes}
                      onChange={onHandleAppointmentType}
                    />
                  )}

                  {consultType === ConsultType.Else && (
                    <>
                      <FormColumns>
                        <FormRow>
                          <InputFieldController
                            icon={BaseInputIcon.DATE}
                            type="date"
                            name="rangeStartDate"
                            label={
                              <>
                                <CustomFormattedMessage id="common.start-date" />
                                <small>*</small>
                              </>
                            }
                            min={formatDate(new Date())}
                            value={rangeStartDate}
                            onChange={onHandleDateRangeStart}
                          />
                        </FormRow>
                        <FormRow>
                          <InputSelectController<TemplateFrequency>
                            name="frequency"
                            label={
                              <>
                                <CustomFormattedMessage id="agenda.appointment-create.frequency" />
                                <small>*</small>
                              </>
                            }
                            options={templateFrequencyData}
                          />
                        </FormRow>
                        {isMultiple && (
                          <FormRow>
                            <InputFieldController
                              icon={BaseInputIcon.DATE}
                              type="date"
                              name="rangeEndDate"
                              label={
                                <>
                                  <CustomFormattedMessage id="common.end-date" />
                                  {isMultiple && <small>*</small>}
                                </>
                              }
                              value={rangeEndDate}
                              min={rangeStartDate ?? formatDate(new Date())}
                              disabled={!isMultiple}
                              onChange={onHandleDateRangeEnd}
                            />
                          </FormRow>
                        )}
                      </FormColumns>
                      <h3>
                        <CustomFormattedMessage id="agenda.appointment.slots" />
                      </h3>

                      {honorific &&
                        ![ConsultType.Telephone].includes(consultType) && (
                          <div className="honorific-block-times">
                            <h4>
                              <CustomFormattedMessage id="common.minutes-select-placeholder" />
                            </h4>
                            <div className="honorific-block-times__content">
                              <InputCheckboxController<boolean>
                                name="irregular"
                                options={[
                                  {
                                    key: true,
                                    title: intl.formatMessage({
                                      id: 'agenda.appointment.irregular',
                                    }),
                                  },
                                ]}
                                value={irregular}
                              />

                              <InputSelectController<number>
                                name="minutes"
                                options={filteredBlockTimes}
                                placeholder={intl.formatMessage({
                                  id: 'common.minutes-select-placeholder',
                                })}
                              />
                            </div>
                          </div>
                        )}
                      {irregular === true && (
                        <Message
                          type={MessageType.WARNING}
                          message={
                            <CustomFormattedMessage id="common.errors.template-override" />
                          }
                        />
                      )}
                      {[ConsultType.Bulk].includes(consultType) ||
                      (![ConsultType.Bulk].includes(consultType) && minutes) ? (
                        <AgendaAvailableBlockList
                          consultType={consultType}
                          showAsRange
                          loading={blocksLoading}
                          items={blocks}
                          date={date}
                          value={{
                            healthProfessionalId,
                            selectedSlots,
                          }}
                          onClick={onHandleSlots}
                        />
                      ) : (
                        <p>
                          <CustomFormattedMessage id="agenda.appointment-create.no-honorific" />
                        </p>
                      )}
                    </>
                  )}

                  {[
                    ConsultType.Physical,
                    ConsultType.Video,
                    ConsultType.Telephone,
                    ConsultType.Visit,
                    ConsultType.Bulk,
                  ].includes(consultType) && (
                    <>
                      <FormColumns>
                        <FormRow>
                          <InputFieldPager
                            onClickPrev={
                              prevDate
                                ? () => onHandleDatePager('prev')
                                : undefined
                            }
                            onClickNext={
                              nextDate
                                ? () => onHandleDatePager('next')
                                : undefined
                            }
                          >
                            <InputField
                              type="date"
                              name="date"
                              label={
                                <>
                                  <CustomFormattedMessage id="common.date" />
                                  <small>*</small>
                                </>
                              }
                              icon={BaseInputIcon.DATE}
                              value={date}
                              min={formatDate(new Date())}
                              onChange={onHandleDate}
                            />
                          </InputFieldPager>
                        </FormRow>

                        {![ConsultType.Bulk].includes(consultType) && (
                          <FormRow>
                            <InputSelectController<Honorific>
                              name="honorific"
                              label={
                                <>
                                  <CustomFormattedMessage id="common.honorific" />
                                  <small>*</small>
                                </>
                              }
                              placeholder={intl.formatMessage({
                                id: 'common.honorific-placeholder',
                              })}
                              options={honorifics}
                              disabled={agendaAppointmentItem?.disableHonorific}
                            />
                          </FormRow>
                        )}
                      </FormColumns>

                      <h3>
                        <CustomFormattedMessage id="agenda.appointment.slots" />
                      </h3>

                      {honorific &&
                        ![ConsultType.Telephone, ConsultType.Bulk].includes(
                          consultType
                        ) && (
                          <div className="honorific-block-times">
                            <h4>
                              <CustomFormattedMessage id="common.minutes-select-placeholder" />
                            </h4>
                            <div className="honorific-block-times__content">
                              <InputCheckboxController<boolean>
                                name="irregular"
                                options={[
                                  {
                                    key: true,
                                    title: intl.formatMessage({
                                      id: 'agenda.appointment.irregular',
                                    }),
                                  },
                                ]}
                                value={irregular}
                              />

                              <InputSelectController<number>
                                name="minutes"
                                options={filteredBlockTimes}
                                placeholder={intl.formatMessage({
                                  id: 'common.minutes-select-placeholder',
                                })}
                              />
                            </div>
                          </div>
                        )}
                      {irregular === true && (
                        <Message
                          type={MessageType.WARNING}
                          message={
                            <CustomFormattedMessage id="common.errors.template-override" />
                          }
                        />
                      )}

                      {[ConsultType.Bulk].includes(consultType) || minutes ? (
                        <>
                          <AgendaAvailableBlockList
                            consultType={consultType}
                            showAsRange
                            loading={blocksLoading}
                            items={blocks}
                            date={date}
                            value={{
                              healthProfessionalId,
                              selectedSlots,
                            }}
                            onClick={onHandleSlots}
                          />

                          {next && (
                            <Button
                              className="first-available-date"
                              testId="first-available-date"
                              type={ButtonType.TEXT}
                              onClick={() => {
                                setDate(next);
                              }}
                            >
                              <CustomFormattedMessage
                                id="agenda.appointment.first-available-date"
                                values={{
                                  date: (
                                    <CustomFormattedDate
                                      value={next}
                                      weekday="long"
                                    />
                                  ),
                                }}
                              />
                            </Button>
                          )}
                        </>
                      ) : (
                        <p>
                          <CustomFormattedMessage id="agenda.appointment-create.no-honorific" />
                        </p>
                      )}
                    </>
                  )}
                </>
              )}

              {formStep === 3 &&
                (consultType === ConsultType.Else ? (
                  <FormRow>
                    <InputTextAreaController
                      name="note"
                      label={
                        <CustomFormattedMessage id="agenda.appointment-create.note" />
                      }
                      placeholder={intl.formatMessage({
                        id: 'agenda.appointment-create.note',
                      })}
                    />
                  </FormRow>
                ) : (
                  <FormRow>
                    <InputTextAreaController
                      name="note"
                      label={
                        <CustomFormattedMessage id="agenda.appointment-create.note-patient" />
                      }
                      placeholder={intl.formatMessage({
                        id: 'agenda.appointment-create.note-patient-placeholder',
                      })}
                    />
                  </FormRow>
                ))}

              {formStep === 4 && (
                <>
                  <h4>
                    <CustomFormattedMessage id="common.health-professional" />
                  </h4>
                  <p>
                    {healthProfessionalItemData ? (
                      getFullName(healthProfessionalItemData)
                    ) : (
                      <CustomFormattedMessage id="agenda.appointment-create.no-health-professional" />
                    )}
                  </p>

                  {consultType !== ConsultType.Else ? (
                    <>
                      <h4>
                        <CustomFormattedMessage id="common.patient" />
                      </h4>
                      <p>
                        {patientItemData ? (
                          <>
                            {getFullName(patientItemData)}
                            <br />
                            <time>{getBirthDate(patientItemData)}</time>
                          </>
                        ) : (
                          <CustomFormattedMessage id="agenda.appointment-create.no-patient" />
                        )}
                      </p>

                      <h4>
                        <CustomFormattedMessage id="common.conversation.conversation-type" />
                      </h4>
                      <p>
                        {!isEmptyOrNone(patientConversationItem?.title) ? (
                          patientConversationItem?.title
                        ) : (
                          <CustomFormattedMessage id="agenda.appointment-create.no-conversation-type" />
                        )}
                      </p>

                      <h4>
                        <CustomFormattedMessage id="agenda.appointment-create.note-patient" />
                      </h4>
                      <p>{!isEmptyOrNone(note) ? note : '-'}</p>
                    </>
                  ) : (
                    <>
                      <h4>
                        <CustomFormattedMessage id="agenda.appointment-create.description" />
                      </h4>
                      <p>{!isEmptyOrNone(title) ? title : '-'}</p>

                      <h4>
                        <CustomFormattedMessage id="agenda.appointment-create.note" />
                      </h4>
                      <p>{!isEmptyOrNone(note) ? note : '-'}</p>
                    </>
                  )}

                  <h4>
                    <CustomFormattedMessage id="agenda.appointment-create.appointment" />
                  </h4>
                  <p>
                    {range.length > 0 ? (
                      <AgendaAppointmentDescription
                        data={{
                          startDate: firstInRange.startDateTime,
                          endDate: lastInRange.endDateTime,
                          frequency,
                          type: consultType,
                        }}
                      />
                    ) : (
                      <CustomFormattedMessage id="agenda.appointment-create.no-date" />
                    )}
                  </p>

                  {consultType === ConsultType.Video && (
                    <>
                      <h4>
                        <CustomFormattedMessage id="common.video-link" />
                      </h4>
                      <p>
                        {healthProfessionalItemData?.meetingRoomLink &&
                        !isEmptyOrNone(
                          healthProfessionalItemData?.meetingRoomLink
                        ) ? (
                          <a
                            href={healthProfessionalItemData?.meetingRoomLink}
                            target="_blank"
                            title={healthProfessionalItemData?.meetingRoomLink}
                            rel="noreferrer"
                          >
                            {healthProfessionalItemData?.meetingRoomLink}
                          </a>
                        ) : (
                          '-'
                        )}
                      </p>
                    </>
                  )}
                </>
              )}
            </>
          )}
        </FormProvider>
      </Modal>

      {cancelPromptOpen && (
        <ModalPrompt
          onConfirm={() => onHandleCancel(true)}
          onClose={() => setCancelPromptOpen(false)}
        >
          <p>
            <CustomFormattedMessage id="common.confirm-not-saved-changes" />
          </p>
        </ModalPrompt>
      )}

      {deleteModalOpen && (
        <AgendaAppointmentDeletePrompt
          agendaAppointmentItem={
            agendaAppointmentItem as unknown as AgendaAppointmentItemDataFragment
          }
          onDeleted={() => {
            onClose?.();
          }}
          onClose={() => {
            setDeleteModalOpen(false);
          }}
        />
      )}
    </>
  );
}
